import { NewLead } from './whatsappService';
import { Lead as ReduxLead, Interaction } from '../redux/slices/leadSlice';
import { v4 as uuidv4 } from 'uuid';
import { store } from '../redux/store';
import { addLead, updateLead, addInteraction } from '../redux/slices/leadSlice';
import notificationService from './notificationService';

// Interface para Lead
export interface Lead {
  id: string;
  name: string;
  phoneNumber: string;
  email?: string;
  source: string;
  profilePicUrl?: string;
  notes?: string;
  tags?: string[];
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  createdAt: Date;
  updatedAt: Date;
  lastContactDate?: Date;
  assignedTo?: string;
  properties?: {
    interestedIn?: string[];
    budget?: {
      min?: number;
      max?: number;
    };
    preferredLocations?: string[];
    preferredContactMethod?: 'whatsapp' | 'phone' | 'email';
    customFields?: Record<string, any>;
  };
}

// Mapeamento de status entre nosso serviço e o Redux
const statusMapping = {
  'new': 'Novo',
  'contacted': 'Contatado',
  'qualified': 'Qualificado',
  'converted': 'Convertido',
  'lost': 'Perdido'
};

// Simulação de API de Leads
const API_URL = 'http://localhost:3001/api/leads';

export const leadService = {
  async getLeads(filters?: {
    status?: Lead['status'];
    source?: string;
    assignedTo?: string;
    tags?: string[];
    page?: number;
    limit?: number;
  }): Promise<{
    leads: Lead[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(`${key}[]`, v));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    
    const response = await fetch(`${API_URL}?${queryParams.toString()}`);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar leads');
    }
    
    return response.json();
  },
  
  async getLeadById(id: string): Promise<Lead> {
    const response = await fetch(`${API_URL}/${id}`);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar lead');
    }
    
    return response.json();
  },
  
  async createLead(newLead: NewLead): Promise<Lead> {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newLead),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao criar lead');
    }
    
    const createdLead = await response.json();
    
    // Criar lead no Redux também
    this.createLeadInRedux(newLead, createdLead.id);
    
    return createdLead;
  },
  
  async updateLead(id: string, updates: Partial<Lead>): Promise<Lead> {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao atualizar lead');
    }
    
    const updatedLead = await response.json();
    
    // Atualizar lead no Redux também
    this.updateLeadInRedux(id, updates);
    
    return updatedLead;
  },
  
  async deleteLead(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error('Erro ao excluir lead');
    }
  },
  
  async findLeadByPhoneNumber(phoneNumber: string): Promise<Lead | null> {
    // Remove caracteres não numéricos para normalizar o número
    const normalizedPhone = phoneNumber.replace(/\D/g, '');
    
    const response = await fetch(`${API_URL}/search?phoneNumber=${normalizedPhone}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Erro ao buscar lead por número de telefone');
    }
    
    return response.json();
  },
  
  async importLeadsFromCSV(file: File): Promise<{
    imported: number;
    errors: number;
    errorDetails?: string[];
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/import`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Erro ao importar leads');
    }
    
    return response.json();
  },
  
  async exportLeads(filters?: {
    status?: Lead['status'];
    source?: string;
    tags?: string[];
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(`${key}[]`, v));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    
    const response = await fetch(`${API_URL}/export?${queryParams.toString()}`);
    
    if (!response.ok) {
      throw new Error('Erro ao exportar leads');
    }
    
    return response.blob();
  },
  
  // Método para criar lead a partir de contato do WhatsApp
  async createLeadFromWhatsApp(newLead: NewLead): Promise<Lead> {
    // Verificar se já existe um lead com este número
    const existingLead = await this.findLeadByPhoneNumber(newLead.phoneNumber);
    
    if (existingLead) {
      // Atualizar lead existente com novas informações
      const updates: Partial<Lead> = {
        updatedAt: new Date(),
        lastContactDate: new Date()
      };
      
      // Atualizar nome se não estiver definido
      if (!existingLead.name || existingLead.name === 'Nome não disponível') {
        updates.name = newLead.name;
      }
      
      // Atualizar e-mail se foi detectado e o existente está vazio ou é diferente
      if (newLead.email && (!existingLead.email || existingLead.email !== newLead.email)) {
        updates.email = newLead.email;
        
        // Adicionar nota sobre a atualização do e-mail
        const emailNote = `E-mail detectado na conversa: ${newLead.email} (${new Date().toLocaleString()})`;
        updates.notes = existingLead.notes 
          ? `${existingLead.notes}\n\n${emailNote}` 
          : emailNote;
      }
      
      // Atualizar foto se disponível
      if (newLead.profilePicUrl) {
        updates.profilePicUrl = newLead.profilePicUrl;
      }
      
      // Atualizar tags se não existirem
      if (newLead.tags) {
        const existingTags = existingLead.tags || [];
        const uniqueTags = Array.from(new Set([...existingTags, ...newLead.tags]));
        updates.tags = uniqueTags;
      }
      
      // Adicionar nota se não foi adicionada na atualização do e-mail
      if (newLead.notes && !updates.notes) {
        updates.notes = existingLead.notes 
          ? `${existingLead.notes}\n\n${newLead.notes}` 
          : newLead.notes;
      }
      
      const updatedLead = await this.updateLead(existingLead.id, updates);
      
      // Adicionar interação no Redux
      this.addInteractionInRedux(existingLead.id, {
        type: 'whatsapp',
        content: newLead.email 
          ? `Contato atualizado via WhatsApp. E-mail detectado: ${newLead.email}` 
          : 'Contato atualizado via WhatsApp'
      });
      
      // Criar notificação de atualização
      notificationService.createNotification({
        title: 'Lead Atualizado',
        message: newLead.email 
          ? `Lead "${updatedLead.name}" foi atualizado com e-mail detectado: ${newLead.email}.`
          : `Lead "${updatedLead.name}" foi atualizado com informações do WhatsApp.`,
        type: 'info',
        link: `/leads/${existingLead.id}`,
        linkText: 'Ver Lead'
      });
      
      return updatedLead;
    }
    
    // Criar novo lead
    const createdLead = await this.createLead({
      ...newLead,
      // Adicionar informações adicionais para novos leads
      tags: [...(newLead.tags || []), 'novo-contato']
    });
    
    return createdLead;
  },
  
  // Método para criar lead a partir de e-mail
  async createLeadFromEmail(newLead: NewLead, emailId?: string): Promise<Lead> {
    // Verificar se já existe um lead com este e-mail
    const existingLead = await this.findLeadByEmail(newLead.email || '');
    
    if (existingLead) {
      // Atualizar lead existente com novas informações
      const updates: Partial<Lead> = {
        updatedAt: new Date(),
        lastContactDate: new Date()
      };
      
      // Atualizar nome se não estiver definido
      if (!existingLead.name || existingLead.name === 'Nome não disponível') {
        updates.name = newLead.name;
      }
      
      // Atualizar número de telefone se foi fornecido e o existente está vazio
      if (newLead.phoneNumber && !existingLead.phoneNumber) {
        updates.phoneNumber = newLead.phoneNumber;
      }
      
      // Atualizar tags se não existirem
      if (newLead.tags) {
        const existingTags = existingLead.tags || [];
        const uniqueTags = Array.from(new Set([...existingTags, ...newLead.tags]));
        updates.tags = uniqueTags;
      }
      
      // Adicionar nota
      if (newLead.notes) {
        updates.notes = existingLead.notes 
          ? `${existingLead.notes}\n\n${newLead.notes}` 
          : newLead.notes;
      }
      
      const updatedLead = await this.updateLead(existingLead.id, updates);
      
      // Adicionar interação no Redux
      this.addInteractionInRedux(existingLead.id, {
        type: 'email',
        content: 'Contato atualizado via E-mail'
      });
      
      // Criar notificação de atualização
      notificationService.createNotification({
        title: 'Lead Atualizado',
        message: `Lead "${updatedLead.name}" foi atualizado com informações de E-mail.`,
        type: 'info',
        link: `/leads/${existingLead.id}`,
        linkText: 'Ver Lead'
      });
      
      return updatedLead;
    }
    
    // Criar novo lead
    const createdLead = await this.createLead({
      ...newLead,
      // Adicionar informações adicionais para novos leads
      tags: [...(newLead.tags || []), 'novo-contato']
    });
    
    // Criar notificação para novo lead
    notificationService.createNotification({
      title: 'Novo Lead',
      message: `Novo lead "${createdLead.name}" foi criado a partir de um e-mail.`,
      type: 'success',
      link: `/leads/${createdLead.id}`,
      linkText: 'Ver Lead'
    });
    
    return createdLead;
  },
  
  // Método para buscar lead por e-mail
  async findLeadByEmail(email: string): Promise<Lead | null> {
    if (!email) return null;
    
    const response = await fetch(`${API_URL}/search?email=${encodeURIComponent(email)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Erro ao buscar lead por e-mail');
    }
    
    return response.json();
  },
  
  // Métodos para integração com o Redux
  
  // Criar lead no Redux
  createLeadInRedux(newLead: NewLead, id?: string): void {
    const leadId = id || uuidv4();
    const uniqueCode = `LD${Math.floor(100000 + Math.random() * 900000)}`;
    
    const reduxLead: ReduxLead = {
      id: leadId,
      uniqueCode,
      name: newLead.name,
      email: newLead.email || '',
      phone: newLead.phoneNumber,
      source: newLead.source || 'WhatsApp',
      status: 'Novo',
      notes: newLead.notes,
      lastContactDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      avatar: newLead.profilePicUrl,
      interactions: [{
        id: uuidv4(),
        date: new Date().toISOString(),
        type: 'whatsapp',
        direction: 'incoming',
        content: 'Lead criado a partir de contato do WhatsApp'
      }]
    };
    
    // Adicionar ao Redux
    store.dispatch(addLead(reduxLead));
    
    // Criar notificação
    notificationService.createNotification({
      title: 'Novo Lead Criado',
      message: `Lead "${newLead.name}" foi criado a partir de um contato do WhatsApp.`,
      type: 'success',
      link: `/leads/${leadId}`,
      linkText: 'Ver Lead'
    });
  },
  
  // Atualizar lead no Redux
  updateLeadInRedux(id: string, updates: Partial<Lead>): void {
    // Buscar lead atual no Redux
    const currentState = store.getState();
    const existingLead = currentState.leads.items.find(lead => lead.id === id);
    
    if (!existingLead) return;
    
    // Mapear atualizações para o formato do Redux
    const reduxUpdates: Partial<ReduxLead> = {};
    
    if (updates.name) reduxUpdates.name = updates.name;
    if (updates.email) reduxUpdates.email = updates.email;
    if (updates.phoneNumber) reduxUpdates.phone = updates.phoneNumber;
    if (updates.profilePicUrl) reduxUpdates.avatar = updates.profilePicUrl;
    if (updates.notes) reduxUpdates.notes = updates.notes;
    if (updates.status) reduxUpdates.status = statusMapping[updates.status] || updates.status;
    if (updates.lastContactDate) reduxUpdates.lastContactDate = updates.lastContactDate.toISOString();
    
    // Atualizar no Redux
    store.dispatch(updateLead({
      ...existingLead,
      ...reduxUpdates
    }));
  },
  
  // Adicionar interação no Redux
  addInteractionInRedux(leadId: string, interactionData: {
    type: 'email' | 'whatsapp' | 'call' | 'meeting' | 'note';
    content: string;
    direction?: 'incoming' | 'outgoing';
    subject?: string;
  }): void {
    const interaction: Interaction = {
      id: uuidv4(),
      date: new Date().toISOString(),
      type: interactionData.type,
      direction: interactionData.direction || 'incoming',
      content: interactionData.content,
      subject: interactionData.subject
    };
    
    // Adicionar ao Redux
    store.dispatch(addInteraction({
      leadId,
      interaction
    }));
  }
};
